/**
 * B2–B4 — textos e regras de EXIBIÇÃO da página de passagem. Nada aqui calcula métrica
 * (invariante 3): taxas e diferença do A/B chegam prontas do servidor; aqui só viram frase.
 */
import { formatNumber, formatPercent, NO_DATA_DASH } from '@mgdlead/ui';

import type {
  RedirectorPage,
  RedirectorPageVariantRow,
  RedirectorPageVariants,
} from '../../lib/api';

/** Os tetos que o SERVIDOR cobra (B4.1) e as recomendações de leitura da spec de UX. */
export const OG_TITLE_MAX = 90;
export const OG_TITLE_RECOMMENDED = 60;
export const OG_DESCRIPTION_MAX = 200;
export const OG_DESCRIPTION_RECOMMENDED = 110;

export const AVISO_CACHE_WHATSAPP = 'O WhatsApp pode levar até um dia para mostrar a prévia nova.';

/** O título que a página usa sem título próprio — o mesmo do servidor. */
export const TITULO_PADRAO = 'Entrar no grupo do WhatsApp';

/** As opções do A/B, em frase de gente. */
export const PERCENT_OPTIONS = [
  { value: '100', label: 'Todos os cliques veem a página' },
  { value: '50', label: 'Metade dos cliques vê a página (teste A/B)' },
  { value: '25', label: '1 em cada 4 cliques vê a página (teste A/B)' },
  { value: '10', label: '1 em cada 10 cliques vê a página (teste A/B)' },
] as const;

/** Os eventos do Pixel que o servidor aceita (lista fechada, B2.3), com nome em português. */
export const PIXEL_EVENTS: ReadonlyArray<{
  value: RedirectorPage['metaPixelEvents'][number];
  label: string;
}> = [
  { value: 'PageView', label: 'PageView (visualização)' },
  { value: 'ViewContent', label: 'ViewContent' },
  { value: 'Lead', label: 'Lead' },
  { value: 'Contact', label: 'Contact (contato)' },
  { value: 'CompleteRegistration', label: 'CompleteRegistration' },
  { value: 'SubmitApplication', label: 'SubmitApplication' },
  { value: 'Subscribe', label: 'Subscribe (inscrição)' },
];

/** Contador de caracteres com o estado: dentro do recomendado, acima dele, acima do teto. */
export function contador(
  texto: string,
  recomendado: number,
  maximo: number,
): { texto: string; estado: 'ok' | 'longo' | 'excedido' } {
  const n = [...texto.normalize('NFC')].length;
  if (n > maximo) return { texto: `${n}/${maximo} — passou do limite`, estado: 'excedido' };
  if (n > recomendado) {
    return { texto: `${n}/${recomendado} — pode ser cortado na prévia`, estado: 'longo' };
  }
  return { texto: `${n}/${recomendado}`, estado: 'ok' };
}

/**
 * A URL pública da imagem da prévia, com o sha como quebra de cache — a mesma que o servidor
 * põe na `og:image`. `null` sem endereço público ou sem imagem.
 */
export function ogImageUrl(publicUrl: string | null, page: Pick<RedirectorPage, 'ogImage'>) {
  if (!publicUrl || !page.ogImage) return null;
  return `${publicUrl.replace(/\/+$/, '')}/og?v=${page.ogImage.sha256.slice(0, 16)}`;
}

/** O host que o card do WhatsApp mostra embaixo do título. */
export function hostDe(publicUrl: string | null): string | null {
  if (!publicUrl) return null;
  try {
    return new URL(publicUrl).host;
  } catch {
    return null;
  }
}

/** A frase do estado da página, para o topo da seção. */
export function estadoDaPagina(page: Pick<RedirectorPage, 'enabled' | 'pagePercent'>): string {
  if (!page.enabled) return 'Desligada: quem clica vai direto para o WhatsApp.';
  if (page.pagePercent >= 100) return 'Ligada: todo clique vê a página antes do WhatsApp.';
  return `Ligada em teste A/B: ${page.pagePercent}% dos cliques veem a página; o resto vai direto.`;
}

export const VARIANTE = {
  PAGE: { label: 'Página', hint: 'Este clique viu a página de passagem antes do WhatsApp.' },
  DIRECT: { label: 'Direto', hint: 'Este clique foi direto para o WhatsApp (sem a página).' },
} as const;

/** "12,5%" ou "—" (razão sem denominador nunca vira 0%). */
export function taxaEmTexto(row: Pick<RedirectorPageVariantRow, 'entryRate'>): string {
  return row.entryRate === null ? NO_DATA_DASH : formatPercent(row.entryRate);
}

/**
 * A frase de leitura do placar. Só formata números do servidor; com amostra pequena, diz que
 * ainda não dá para concluir em vez de apontar um vencedor.
 */
export function leituraDoPlacar(placar: RedirectorPageVariants): string {
  const { page, direct, difference } = placar;
  if (page.clicks === 0 && direct.clicks === 0) {
    return 'Ainda não há clique no teste. Assim que alguém clicar, comparamos os dois caminhos aqui.';
  }
  if (difference === null) {
    return 'Só um dos caminhos recebeu clique até agora — ainda não há o que comparar.';
  }
  if (page.smallSample || direct.smallSample) {
    const menor = Math.min(page.clicks, direct.clicks);
    return `Pouca gente para tirar conclusão (${formatNumber(menor)} cliques no caminho com menos). Precisamos de pelo menos ${formatNumber(placar.minClicks)} em cada.`;
  }
  const pontos = formatNumber(Math.abs(difference) * 100, { decimals: 1 });
  if (Math.abs(difference) < 0.0005) return 'Os dois caminhos estão empatados até aqui.';
  return difference > 0
    ? `Com a página, ${pontos} pontos a mais de entradas por clique.`
    : `Direto para o WhatsApp, ${pontos} pontos a mais de entradas por clique.`;
}

/* -------------------------------------------------------------------------- */
/*  Imagem: o navegador redimensiona para 1200×630 antes de enviar             */
/* -------------------------------------------------------------------------- */

export const OG_WIDTH = 1200;
export const OG_HEIGHT = 630;
/** Teto do envio, igual ao do servidor (700 KB). */
export const OG_UPLOAD_MAX_BYTES = 700 * 1024;

/** Recorte "cover": centraliza e corta o que sobra para caber em 1200×630. */
export function coverCrop(
  w: number,
  h: number,
): { sx: number; sy: number; sw: number; sh: number } {
  const alvo = OG_WIDTH / OG_HEIGHT;
  if (w / h > alvo) {
    const sw = Math.round(h * alvo);
    return { sx: Math.round((w - sw) / 2), sy: 0, sw, sh: h };
  }
  const sh = Math.round(w / alvo);
  return { sx: 0, sy: Math.round((h - sh) / 2), sw: w, sh };
}

/**
 * Lê o arquivo escolhido, recorta em "cover" e redimensiona para 1200×630 num `<canvas>`,
 * devolvendo o JPEG em base64 (sem o prefixo `data:`). O SERVIDOR confere os magic bytes e
 * re-escreve a imagem de qualquer jeito (B4.1) — isto aqui só poupa banda e o teto do envio.
 */
export async function prepararImagem(arquivo: File): Promise<string> {
  if (!/^image\/(png|jpeg|webp)$/.test(arquivo.type)) {
    throw new Error('Use uma imagem PNG, JPEG ou WebP.');
  }
  const bitmap = await createImageBitmap(arquivo);
  const canvas = document.createElement('canvas');
  canvas.width = OG_WIDTH;
  canvas.height = OG_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Este navegador não conseguiu preparar a imagem.');
  const { sx, sy, sw, sh } = coverCrop(bitmap.width, bitmap.height);
  ctx.drawImage(bitmap, sx, sy, sw, sh, 0, 0, OG_WIDTH, OG_HEIGHT);
  bitmap.close();
  for (const qualidade of [0.85, 0.7, 0.55]) {
    const dataUrl = canvas.toDataURL('image/jpeg', qualidade);
    const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
    if (base64ToBytes(base64) <= OG_UPLOAD_MAX_BYTES) return base64;
  }
  throw new Error('A imagem ficou pesada demais mesmo comprimida. Tente uma mais simples.');
}

/** Tamanho em bytes de um base64 (sem decodificar). */
export function base64ToBytes(base64: string): number {
  const pad = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - pad;
}
